import { useEffect, useState } from 'react';
import { structuresApi } from '../api/structures';
import { structureClaimsApi } from '../api/structureClaims';
import { useAuthStore } from '../store/authStore';
import { createPrefixedLogger } from '../utils/logger';
import { Structure, StructureClaim } from '../types';

const log = createPrefixedLogger('useStructureWithClaim');

interface UseStructureWithClaimResult {
  structure: Structure | null;
  claim: StructureClaim | null;
  /**
   * True once the claim subscription has delivered its first snapshot for
   * this (structureId, userId) pair — the point where `claim: null` means
   * "verified unclaimed" rather than "not looked yet". Stays false while
   * signed out (no subscription runs). The try-then-save auto-claim gates on
   * this so it never races a claim doc that just hasn't arrived.
   */
  claimResolved: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Subscribe to both a structure doc and its claim doc. Returns live data
 * for both. Loading is true until the structure resolves (claim may be null
 * if unclaimed).
 */
export function useStructureWithClaim(
  structureId: string | undefined
): UseStructureWithClaimResult {
  const [structure, setStructure] = useState<Structure | null>(null);
  const [claim, setClaim] = useState<StructureClaim | null>(null);
  const [claimResolved, setClaimResolved] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // structureClaims requires auth in Firestore rules, but this hook also
  // backs the public (signed-out) structure view reached via shared links.
  // Subscribe to the claim only when signed in; keying on the uid (not the
  // user object) re-subscribes if the viewer signs in mid-view.
  const userId = useAuthStore((s) => s.user?.id);

  useEffect(() => {
    if (!structureId) return;
    setIsLoading(true);
    setError(null);

    const unsubStructure = structuresApi.subscribeToStructure(
      structureId,
      (result) => {
        setStructure(result);
        setIsLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load property');
        setIsLoading(false);
      }
    );

    let unsubClaim: (() => void) | undefined;
    setClaimResolved(false);
    if (userId) {
      unsubClaim = structureClaimsApi.subscribeToClaim(
        structureId,
        (result) => {
          setClaim(result);
          setClaimResolved(true);
        },
        (err) => {
          // A claim failure must not blank the whole property screen — the
          // structure itself loaded fine. Render as unclaimed and log.
          // Counts as resolved: a downstream claim create against a doc that
          // does exist just fails at the rules, it can't corrupt anything.
          log.warn('claim subscription failed', err);
          setClaim(null);
          setClaimResolved(true);
        }
      );
    } else {
      setClaim(null);
    }

    return () => {
      unsubStructure();
      unsubClaim?.();
    };
  }, [structureId, userId]);

  return { structure, claim, claimResolved, isLoading, error };
}
